import { useState, type FormEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../api/client';
import { putStaffPayRate, type PayRateInput } from '../api/endpoints/payroll';
import type { PayBasis, PayrollRow } from '../api/schemas';
import { centsToDollarsInput } from '../lib/money';
import { parseRateInput } from '../lib/payroll';
import { useMutationAttempt } from '../lib/use-mutation-attempt';
import { FormField } from './FormField';

type PayRateEditorProps = {
  row: PayrollRow;
  onDone: () => void;
};

const BASIS_OPTIONS: readonly { id: PayBasis; label: string }[] = [
  { id: 'class', label: 'Por clase' },
  { id: 'hour', label: 'Por hora' },
];

const BASIS_MISSING = 'Elige si se paga por clase o por hora.';
const SAVE_FAILED = 'No pudimos guardar la tarifa. Revisa tu conexión e intenta de nuevo.';
/** The rate has no history on the server: say so where it is changed. */
const RATE_HINT = 'Vale para toda planilla que abras después, también las pasadas. Descarga el CSV al cierre para guardarla.';

type SaveInput = { staffId: string; rate: PayRateInput };

/**
 * One coach's rate, edited inline in the Planilla: dollars typed, integer
 * cents sent, with its basis. "Quitar tarifa" sends both null. The planilla
 * reloads after a save, since the rate re-prices the whole period.
 */
export function PayRateEditor({ row, onDone }: PayRateEditorProps) {
  const queryClient = useQueryClient();
  const [amount, setAmount] = useState(row.rateCents === null ? '' : centsToDollarsInput(row.rateCents));
  const [basis, setBasis] = useState<PayBasis | null>(row.basis);
  const [amountError, setAmountError] = useState<string>();
  const [basisError, setBasisError] = useState<string>();
  const inputId = `tarifa-${row.staffId}`;

  const save = useMutationAttempt<SaveInput, unknown>({
    mutationFn: (input, key) => putStaffPayRate(input.staffId, input.rate, key),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['business-payroll'] });
      onDone();
    },
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const parsed = parseRateInput(amount);
    setAmountError(parsed.ok ? undefined : parsed.message);
    setBasisError(basis ? undefined : BASIS_MISSING);
    if (!parsed.ok || !basis) return;
    save.mutate({ staffId: row.staffId, rate: { rateCents: parsed.cents, basis } });
  };

  const clear = () => save.mutate({ staffId: row.staffId, rate: { rateCents: null, basis: null } });

  return (
    <form className="payroll-rate-form" aria-label={`Tarifa de ${row.name}`} onSubmit={submit} noValidate>
      <FormField label="Monto en dólares" htmlFor={inputId} error={amountError}>
        <input
          id={inputId}
          inputMode="decimal"
          autoComplete="off"
          placeholder="25.00"
          aria-describedby={`${inputId}-note`}
          value={amount}
          onChange={(event) => setAmount(event.target.value)}
        />
      </FormField>
      <div className="field">
        <div className="seg" role="group" aria-label="Se paga">
          {BASIS_OPTIONS.map((option) => (
            <button
              key={option.id}
              type="button"
              aria-pressed={basis === option.id}
              onClick={() => setBasis(option.id)}
            >
              {option.label}
            </button>
          ))}
        </div>
        {basisError ? (
          <p role="alert" className="field-error">
            {basisError}
          </p>
        ) : null}
      </div>
      <div className="payroll-rate-actions">
        <button type="submit" className="btn small" disabled={save.isPending}>
          {save.isPending ? 'Guardando…' : 'Guardar'}
        </button>
        <button type="button" className="btn small ghost" onClick={onDone} disabled={save.isPending}>
          Cancelar
        </button>
        {row.rateCents !== null ? (
          <button type="button" className="btn small ghost" onClick={clear} disabled={save.isPending}>
            Quitar tarifa
          </button>
        ) : null}
      </div>
      <p className="row-sub payroll-rate-note" id={`${inputId}-note`}>
        {RATE_HINT}
      </p>
      {save.isError ? (
        <p role="alert" className="field-error payroll-rate-error">
          {save.error instanceof ApiError ? save.error.message : SAVE_FAILED}
        </p>
      ) : null}
    </form>
  );
}
